import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTenant } from '../contexts/TenantContext';

const MicrositeDomains = ({ apiBase }) => {
  const { tenant } = useTenant();

  const [domains, setDomains] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [generatingMicrosite, setGeneratingMicrosite] = useState(null);
  const [micrositePreview, setMicrositePreview] = useState(null);
  const [formData, setFormData] = useState({
    domain_name: '',
    category: '',
    rationale: '',
    focus: '',
    content_strategy: '',
    hosting_provider: '',
    ip_range: '',
    cdn_provider: '',
    dns_provider: '',
    cms_framework: '',
    status: 'registered',
    ssl_enabled: false,
    email_forwarding: false,
    registered_date: '',
    expiry_date: '',
    project_id: '',
    notes: ''
  });

  useEffect(() => {
    fetchDomains();
    fetchProjects();
  }, []);

  const fetchDomains = async () => {
    try {
      const response = await axios.get(`${apiBase}/microsite-domains`);
      setDomains(response.data);
    } catch (error) {
      console.error('Error fetching domains:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${apiBase}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDomain) {
        await axios.put(`${apiBase}/microsite-domains/${editingDomain.id}`, formData);
      } else {
        await axios.post(`${apiBase}/microsite-domains`, formData);
      }
      setShowModal(false);
      setEditingDomain(null);
      resetForm();
      fetchDomains();
    } catch (error) {
      console.error('Error saving domain:', error);
      alert('Error saving domain: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEdit = (domain) => {
    setEditingDomain(domain);
    setFormData({
      domain_name: domain.domain_name || '',
      category: domain.category || '',
      rationale: domain.rationale || '',
      focus: domain.focus || '',
      content_strategy: domain.content_strategy || '',
      hosting_provider: domain.hosting_provider || '',
      ip_range: domain.ip_range || '',
      cdn_provider: domain.cdn_provider || '',
      dns_provider: domain.dns_provider || '',
      cms_framework: domain.cms_framework || '',
      status: domain.status || 'registered',
      ssl_enabled: domain.ssl_enabled || false,
      email_forwarding: domain.email_forwarding || false,
      registered_date: domain.registered_date || '',
      expiry_date: domain.expiry_date || '',
      project_id: domain.project_id || '',
      notes: domain.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this domain?')) return;
    
    try {
      await axios.delete(`${apiBase}/microsite-domains/${id}`);
      fetchDomains();
    } catch (error) {
      console.error('Error deleting domain:', error);
      alert('Error deleting domain: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleGenerateMicrosite = async (domainId) => {
    setGeneratingMicrosite(domainId);
    try {
      const response = await axios.post(`${apiBase}/microsite-domains/${domainId}/generate-microsite`);
      setMicrositePreview(response.data.microsite);
      alert('Microsite generated successfully!');
      fetchDomains();
    } catch (error) {
      console.error('Error generating microsite:', error);
      alert('Error generating microsite: ' + (error.response?.data?.error || error.message));
    } finally {
      setGeneratingMicrosite(null);
    }
  };

  const resetForm = () => {
    setFormData({
      domain_name: '',
      category: '',
      rationale: '',
      focus: '',
      content_strategy: '',
      hosting_provider: '',
      ip_range: '',
      cdn_provider: '',
      dns_provider: '',
      cms_framework: '',
      status: 'registered',
      ssl_enabled: false,
      email_forwarding: false,
      registered_date: '',
      expiry_date: '',
      project_id: '',
      notes: ''
    });
  };

  if (loading) {
    return <div className="loading">Loading domains...</div>;
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Microsite Domains Management</h2>
          <button className="btn btn-primary" onClick={() => { setEditingDomain(null); resetForm(); setShowModal(true); }}>
            + Add Domain
          </button>
        </div>

        <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
          <h3 style={{ marginBottom: '10px' }}>📊 Domain Statistics</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
            <div>
              <strong style={{ fontSize: '24px', color: '#667eea' }}>{domains.length}</strong>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Domains</div>
            </div>
            <div>
              <strong style={{ fontSize: '24px', color: '#10b981' }}>{domains.filter(d => d.microsite_generated).length}</strong>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Microsites Generated</div>
            </div>
            <div>
              <strong style={{ fontSize: '24px', color: '#f59e0b' }}>{domains.filter(d => d.status === 'active').length}</strong>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Active Domains</div>
            </div>
            <div>
              <strong style={{ fontSize: '24px', color: '#8b5cf6' }}>{new Set(domains.map(d => d.hosting_provider)).size}</strong>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Hosting Providers</div>
            </div>
          </div>
        </div>

        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Domain Name</th>
                <th>Category</th>
                <th>Mobile</th>
                <th>Hosting</th>
                <th>IP Address</th>
                <th>Registrar</th>
                <th>Microsite</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((domain) => (
                <tr key={domain.id}>
                  <td>
                    <strong style={{ color: '#667eea' }}>{domain.domain_name}</strong>
                    {domain.location_features && (
                      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                        📍 {domain.location_features.substring(0, 60)}...
                      </div>
                    )}
                    {domain.microsite_url && (
                      <div style={{ marginTop: '4px' }}>
                        <a href={domain.microsite_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#10b981' }}>
                          🔗 View Microsite
                        </a>
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="badge" style={{ backgroundColor: '#e0e7ff', color: '#4338ca' }}>
                      {domain.category || '-'}
                    </span>
                  </td>
                  <td>
                    <div style={{ fontSize: '12px' }}>
                      <div>📱 {domain.mobile_number || '-'}</div>
                      {domain.alternate_mobile && (
                        <div style={{ color: '#64748b', marginTop: '2px' }}>📱 {domain.alternate_mobile}</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontSize: '12px' }}>
                      <div><strong>{domain.hosting_provider || '-'}</strong></div>
                      {domain.hosting_plan && (
                        <div style={{ color: '#64748b', fontSize: '11px' }}>{domain.hosting_plan}</div>
                      )}
                      {domain.hosting_cost && (
                        <div style={{ color: '#10b981', fontSize: '11px' }}>₹{domain.hosting_cost}/mo</div>
                      )}
                    </div>
                  </td>
                  <td style={{ fontSize: '11px', fontFamily: 'monospace' }}>
                    {domain.hosting_ip_address || domain.ip_range || '-'}
                  </td>
                  <td>
                    <div style={{ fontSize: '12px' }}>
                      <div>{domain.domain_registrar || '-'}</div>
                      {domain.domain_registration_date && (
                        <div style={{ color: '#64748b', fontSize: '11px' }}>
                          Reg: {new Date(domain.domain_registration_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    {domain.microsite_generated ? (
                      <span className="badge badge-success">✓ Generated</span>
                    ) : (
                      <span className="badge badge-secondary">Not Generated</span>
                    )}
                    {domain.amazon_ses_verified_email && (
                      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                        📧 {domain.amazon_ses_verified_email}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={`badge badge-${domain.status === 'active' ? 'success' : domain.status === 'registered' ? 'info' : 'warning'}`}>
                      {domain.status}
                    </span>
                    {domain.ssl_enabled && (
                      <div style={{ marginTop: '4px' }}>
                        <span className="badge badge-success" style={{ fontSize: '10px' }}>🔒 SSL</span>
                      </div>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      {!domain.microsite_generated && (
                        <button 
                          className="btn btn-sm btn-primary" 
                          onClick={() => handleGenerateMicrosite(domain.id)}
                          disabled={generatingMicrosite === domain.id}
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                        >
                          {generatingMicrosite === domain.id ? 'Generating...' : 'Generate Microsite'}
                        </button>
                      )}
                      <button 
                        className="btn btn-sm btn-secondary" 
                        onClick={() => handleEdit(domain)}
                        style={{ fontSize: '11px', padding: '4px 8px' }}
                      >
                        Edit
                      </button>
                      <button 
                        className="btn btn-sm btn-danger" 
                        onClick={() => handleDelete(domain.id)}
                        style={{ fontSize: '11px', padding: '4px 8px' }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setEditingDomain(null); resetForm(); }} style={{ zIndex: 10000 }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3 className="modal-title">{editingDomain ? 'Edit Domain' : 'Add New Domain'}</h3>
              <button className="modal-close" onClick={() => { setShowModal(false); setEditingDomain(null); resetForm(); }}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label className="form-label">Domain Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.domain_name}
                    onChange={(e) => setFormData({ ...formData, domain_name: e.target.value })}
                    required
                    placeholder="e.g., project-name.com"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select
                    className="form-input"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="">Select Category</option>
                    <option value="Project Name + Location">Project Name + Location</option>
                    <option value="Location-First">Location-First</option>
                    <option value="Lifestyle & Benefits">Lifestyle & Benefits</option>
                    <option value="Investment & ROI">Investment & ROI</option>
                    <option value="Unit-Specific">Unit-Specific</option>
                    <option value="Developer & Brand">Developer & Brand</option>
                  </select>
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Rationale</label>
                  <textarea
                    className="form-input"
                    rows="2"
                    value={formData.rationale}
                    onChange={(e) => setFormData({ ...formData, rationale: e.target.value })}
                    placeholder="Why this domain was chosen..."
                  />
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Focus</label>
                  <textarea
                    className="form-input"
                    rows="2"
                    value={formData.focus}
                    onChange={(e) => setFormData({ ...formData, focus: e.target.value })}
                    placeholder="What this domain focuses on..."
                  />
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Content Strategy</label>
                  <textarea
                    className="form-input"
                    rows="3"
                    value={formData.content_strategy}
                    onChange={(e) => setFormData({ ...formData, content_strategy: e.target.value })}
                    placeholder="Content strategy for this domain..."
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Hosting Provider</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.hosting_provider}
                    onChange={(e) => setFormData({ ...formData, hosting_provider: e.target.value })}
                    placeholder="e.g., AWS, GCP, Azure"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">IP Range</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.ip_range}
                    onChange={(e) => setFormData({ ...formData, ip_range: e.target.value })}
                    placeholder="e.g., 192.168.1.0/24"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">CDN Provider</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.cdn_provider}
                    onChange={(e) => setFormData({ ...formData, cdn_provider: e.target.value })}
                    placeholder="e.g., CloudFlare, CloudFront"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">DNS Provider</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.dns_provider}
                    onChange={(e) => setFormData({ ...formData, dns_provider: e.target.value })}
                    placeholder="e.g., CloudFlare, Route53"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">CMS Framework</label>
                  <select
                    className="form-input"
                    value={formData.cms_framework}
                    onChange={(e) => setFormData({ ...formData, cms_framework: e.target.value })}
                  >
                    <option value="">Select Framework</option>
                    <option value="WordPress">WordPress</option>
                    <option value="Next.js">Next.js</option>
                    <option value="Gatsby">Gatsby</option>
                    <option value="Hugo">Hugo</option>
                    <option value="Jekyll">Jekyll</option>
                    <option value="React">React</option>
                    <option value="Vue.js">Vue.js</option>
                    <option value="Custom">Custom</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-input"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="registered">Registered</option>
                    <option value="active">Active</option>
                    <option value="development">Development</option>
                    <option value="pending">Pending</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Project</label>
                  <select
                    className="form-input"
                    value={formData.project_id}
                    onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                  >
                    <option value="">Select Project</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Registered Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.registered_date}
                    onChange={(e) => setFormData({ ...formData, registered_date: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Expiry Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                  />
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1', display: 'flex', gap: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.ssl_enabled}
                      onChange={(e) => setFormData({ ...formData, ssl_enabled: e.target.checked })}
                    />
                    SSL Enabled
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.email_forwarding}
                      onChange={(e) => setFormData({ ...formData, email_forwarding: e.target.checked })}
                    />
                    Email Forwarding
                  </label>
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-input"
                    rows="3"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes..."
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" className="btn btn-primary">
                  {editingDomain ? 'Update Domain' : 'Create Domain'}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => { setShowModal(false); setEditingDomain(null); resetForm(); }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {micrositePreview && (
        <div className="modal-overlay" onClick={() => setMicrositePreview(null)} style={{ zIndex: 10001 }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3 className="modal-title">Microsite Preview - {micrositePreview.domain}</h3>
              <button className="modal-close" onClick={() => setMicrositePreview(null)}>×</button>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                <h4>Microsite Details</h4>
                <p><strong>URL:</strong> <a href={micrositePreview.url} target="_blank" rel="noopener noreferrer">{micrositePreview.url}</a></p>
                <p><strong>Template:</strong> {micrositePreview.template}</p>
                <p><strong>Contact:</strong> {micrositePreview.contact.mobile}</p>
                <p><strong>Email:</strong> {micrositePreview.contact.email}</p>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <h4>Project Information</h4>
                <p><strong>Name:</strong> {micrositePreview.project.name}</p>
                <p><strong>Developer:</strong> {micrositePreview.project.developer}</p>
                <p><strong>Location:</strong> {micrositePreview.project.location}</p>
                <p><strong>Towers:</strong> {micrositePreview.project.towers}</p>
                <p><strong>Available Units:</strong> {micrositePreview.inventory.available_units}</p>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <h4>Location Features</h4>
                <p>{micrositePreview.location_features}</p>
              </div>
              <div>
                <h4>Available Units Preview</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                  {micrositePreview.inventory.units.map((unit, idx) => (
                    <div key={idx} style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }}>
                      <strong>{unit.unit_number}</strong> - {unit.bhk_type}
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        Area: {unit.carpet_area} sq ft
                      </div>
                      {unit.final_price && (
                        <div style={{ color: '#10b981', fontWeight: 'bold' }}>
                          ₹{unit.final_price.toLocaleString('en-IN')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                <button 
                  className="btn btn-primary"
                  onClick={() => window.open(micrositePreview.url, '_blank')}
                >
                  Open Microsite
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setMicrositePreview(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MicrositeDomains;

